import { useCallback, useEffect, useState } from "react";
import { Loader2, Paperclip, Undo2 } from "lucide-react";
import { api, apiError, fileUrl, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/Leads";
import { StatusPill } from "@/components/StockStatus";
import { toast } from "sonner";

export default function ItemLedger({ itemId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [correcting, setCorrecting] = useState(null);
  const [form, setForm] = useState({ reason: "", corrected_quantity: "", remarks: "" });
  const [aliasDraft, setAliasDraft] = useState(null);

  const load = useCallback(async () => {
    if (!itemId) return;
    try {
      const { data: d } = await api.get(`/inventory/items/${itemId}/ledger`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
      onClose?.();
    }
  }, [itemId, onClose]);

  useEffect(() => { setData(null); load(); }, [load]);

  const submitCorrection = async () => {
    if (!form.reason.trim()) return toast.error("A reason is required");
    try {
      await api.post("/inventory/corrections", {
        transaction_id: correcting.id,
        reason: form.reason,
        remarks: form.remarks,
        corrected_quantity: form.corrected_quantity !== "" ? Number(form.corrected_quantity) : undefined,
      });
      toast.success("Correction request sent for Director approval");
      setCorrecting(null);
      setForm({ reason: "", corrected_quantity: "", remarks: "" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <Dialog open={!!itemId} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="item-ledger-dialog">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              {data?.item?.name || "Item ledger"}
              {data?.item?.status && <StatusPill status={data.item.status} testId="ledger-status-pill" />}
            </DialogTitle>
          </DialogHeader>
          {!data ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                {data.item.category} • {data.item.warehouse_name} • reorder{" "}
                {data.item.levels.reorder || 0} • min {data.item.levels.minimum || 0} • safety{" "}
                {data.item.levels.safety || 0} • max {data.item.levels.maximum || 0}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                {[
                  ["Opening", data.opening_stock, "ledger-opening"],
                  ["Purchases", data.purchases, "ledger-purchases"],
                  ["Issues", data.issues, "ledger-issues"],
                  ["Transfers", data.transfers, "ledger-transfers"],
                  ["Adjustments", data.adjustments, "ledger-adjustments"],
                  ["Current stock", data.current_stock, "ledger-current"],
                  ...(data.can_see_cost ? [["Value", data.inventory_value, "ledger-value"]] : []),
                ].map(([label, value, tid]) => (
                  <div key={label} className="rounded border border-border p-2" data-testid={tid}>
                    <div className="overline text-[9px]">{label}</div>
                    <div className="font-mono-num text-sm font-medium">
                      {label === "Value" ? inr(value) : `${value} ${data.item.unit || ""}`}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded border border-border p-2.5" data-testid="ledger-aliases">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <span className="overline text-[9px]">Other names staff can search</span>
                  {data.can_approve && aliasDraft === null && (
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                      data-testid="ledger-edit-aliases"
                      onClick={() => setAliasDraft((data.item.aliases || []).join(", "))}>
                      Edit
                    </Button>
                  )}
                </div>
                {aliasDraft === null ? (
                  <div className="flex flex-wrap gap-1">
                    {(data.item.aliases || []).length === 0 ? (
                      <span className="text-[11px] text-muted-foreground">
                        None yet — searching only works on the official name.
                      </span>
                    ) : (
                      data.item.aliases.map((a) => (
                        <Badge key={a} variant="secondary" className="text-[10px]">{a}</Badge>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Input className="h-8 max-w-sm" data-testid="ledger-alias-input"
                      placeholder='1 inch pipe, MS 1 inch pipe, 1" MS pipe'
                      value={aliasDraft} onChange={(e) => setAliasDraft(e.target.value)} />
                    <Button size="sm" className="h-8 px-2 text-[10px]"
                      data-testid="ledger-alias-save"
                      onClick={async () => {
                        try {
                          await api.put(`/inventory/items/${itemId}/aliases`, {
                            aliases: aliasDraft.split(",").map((s) => s.trim()).filter(Boolean),
                          });
                          toast.success("Other names saved — stock and history unchanged");
                          setAliasDraft(null);
                          load();
                          onChanged?.();
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-[10px]"
                      onClick={() => setAliasDraft(null)}>Cancel</Button>
                    <span className="text-[10px] text-muted-foreground">
                      Comma separated. Names only — never a second stock record.
                    </span>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto rounded border border-border">
                <table className="w-full text-xs" data-testid="ledger-rows">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-2 pl-2">Date</th>
                      <th className="py-2 pr-2">Type</th>
                      <th className="py-2 pr-2 text-right">Qty</th>
                      <th className="py-2 pr-2 text-right">Balance</th>
                      <th className="hidden py-2 pr-2 md:table-cell">Reason</th>
                      <th className="py-2 pr-2">By</th>
                      <th className="py-2 pr-2 text-right">Bill</th>
                      <th className="py-2 pr-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.id} className="border-b border-border last:border-0"
                        data-testid={`ledger-row-${r.id}`}>
                        <td className="py-1.5 pl-2">{String(r.date).slice(0, 16).replace("T", " ")}</td>
                        <td className="py-1.5 pr-2">
                          <Badge variant="outline" className="text-[9px]">{r.kind}</Badge>
                          {r.corrected && (
                            <Badge variant="secondary" className="ml-1 text-[9px]">corrected</Badge>
                          )}
                        </td>
                        <td className={`py-1.5 pr-2 text-right font-mono-num ${
                          Number(r.quantity) < 0 ? "text-destructive" : "text-emerald-600"}`}>
                          {r.quantity}
                        </td>
                        <td className="py-1.5 pr-2 text-right font-mono-num">{r.balance}</td>
                        <td className="hidden max-w-[14rem] py-1.5 pr-2 md:table-cell">
                          {r.reason}
                          {r.correction_note && (
                            <div className="text-[10px] text-muted-foreground">{r.correction_note}</div>
                          )}
                        </td>
                        <td className="py-1.5 pr-2">{r.by}</td>
                        <td className="py-1.5 pr-2 text-right">
                          {(r.files || []).map((f) => (
                            <a key={f.file_id} href={fileUrl(f.url)} target="_blank" rel="noreferrer"
                              title={r.invoice_no || f.filename}
                              data-testid={`ledger-bill-${f.file_id}`}
                              className="inline-flex items-center gap-1 rounded border border-border px-1 py-0.5 hover:bg-secondary">
                              <Paperclip className="h-3 w-3" />
                            </a>
                          ))}
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          {data.can_request_correction && !r.corrected && r.kind !== "opening" && (
                            <Button size="sm" variant="ghost" className="h-6 gap-1 px-1.5 text-[10px]"
                              data-testid={`ledger-correct-${r.id}`}
                              onClick={() => {
                                setCorrecting(r);
                                setForm({ reason: "", corrected_quantity: Math.abs(r.quantity), remarks: "" });
                              }}>
                              <Undo2 className="h-3 w-3" /> Correction
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {data.rows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-muted-foreground">
                          No movement recorded for this item yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!correcting} onOpenChange={(o) => !o && setCorrecting(null)}>
        <DialogContent data-testid="correction-request-dialog">
          <DialogHeader>
            <DialogTitle>Request correction</DialogTitle>
          </DialogHeader>
          <div className="rounded border border-border bg-secondary/40 p-3 text-xs">
            {correcting?.kind} of {correcting?.quantity} {data?.item?.unit} on{" "}
            {String(correcting?.date || "").slice(0, 10)} — the original entry stays in the ledger.
            {data?.can_approve
              ? " As a Director you can approve it yourself from the Corrections tab."
              : " A Director must approve before stock changes."}
          </div>
          <Field label={`Correct quantity (${data?.item?.unit || "unit"})`}>
            <Input type="number" data-testid="request-correct-qty-input"
              value={form.corrected_quantity}
              onChange={(e) => setForm({ ...form, corrected_quantity: e.target.value })} />
          </Field>
          <Field label="Reason (required)">
            <Input data-testid="request-correct-reason-input" value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Field>
          <Field label="Remarks">
            <Input data-testid="request-correct-remarks-input" value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </Field>
          <DialogFooter>
            <Button data-testid="request-correct-submit" onClick={submitCorrection}>
              Send for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
